import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import {
  cancelTimeOffRequest,
  createTimeOffRequest,
  fetchTimeOffRequests,
  fetchVacationBalances,
  reviewTimeOffRequest,
  updateTimeOffRequest,
} from "../../../api/TimeTracking/timeTrackingApi";
import {
  ttFilterFieldSx,
  ttFormBlockSx,
  ttKpiChipSx,
  ttResponsiveTableSx,
  ttSectionTitleSx,
  ttTableContainerSx,
} from "./timeTrackingStyles";
import {
  MobileActionGrid,
  MobileCardMetaRow,
  MobileEmptyState,
  MobileRecordCard,
  statusAccentFromMuiColor,
} from "./TimeTrackingMobileCards";
import TabContentShell from "./TabContentShell";
import {
  VacationBalanceCards,
  VacationBalancesTable,
} from "./VacationBalancePanel";
import YearAbsenceCalendar from "./YearAbsenceCalendar";
import { countCarryoverVacationDays } from "./yearAbsenceCalendarUtils";
import { canCancelTimeOffRow, canChangeTimeOffTypeRow } from "./timeOffActions";

const REQUEST_TYPES = [
  { value: "vacation", label: "Vacaciones" },
  { value: "personal_leave", label: "Permiso personal" },
  { value: "sick_leave", label: "Baja / enfermedad" },
  { value: "other_absence", label: "Otra ausencia" },
  { value: "remote_day", label: "Día teletrabajo" },
];

const STATUS_LABEL = {
  pending: "Pendiente",
  approved: "Aprobada",
  rejected: "Rechazada",
  cancelled: "Cancelada",
};

const STATUS_COLOR = {
  pending: "warning",
  approved: "success",
  rejected: "error",
  cancelled: "default",
};

function buildEmployeeNameMap(balances, employees = []) {
  const map = new Map();
  const add = (id, name) => {
    if (id != null && name) map.set(Number(id), name);
  };
  for (const emp of employees) {
    add(emp.id, emp.username);
  }
  for (const emp of balances?.employees || []) {
    add(emp.userId ?? emp.id, emp.username);
  }
  return map;
}

function resolveEmployeeName(row, employeeNameById) {
  return (
    row.employee?.username ||
    row.username ||
    employeeNameById.get(Number(row.userId)) ||
    "—"
  );
}

const TimeOffTab = ({
  selectedUserId,
  canRrhh,
  canFichaje,
  employees = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [rows, setRows] = useState([]);
  const [balances, setBalances] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    requestType: "vacation",
    startDate: "",
    endDate: "",
    reason: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { year };
      if (selectedUserId) params.userId = selectedUserId;
      const [requests, balanceData] = await Promise.all([
        fetchTimeOffRequests(selectedUserId ? { userId: selectedUserId } : {}),
        fetchVacationBalances(params),
      ]);
      setRows(requests);
      setBalances(balanceData);
    } catch (e) {
      setError(e?.response?.data?.error || "Error cargando solicitudes");
    } finally {
      setLoading(false);
    }
  }, [selectedUserId, year]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    if (!form.startDate || !form.endDate) {
      setError("Indica fechas de inicio y fin");
      return;
    }
    try {
      setSaving(true);
      setError(null);
      await createTimeOffRequest(form);
      setForm({
        requestType: "vacation",
        startDate: "",
        endDate: "",
        reason: "",
      });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Error creando solicitud");
    } finally {
      setSaving(false);
    }
  };

  const handleReview = async (id, status) => {
    try {
      await reviewTimeOffRequest(id, { status });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Error revisando solicitud");
    }
  };

  const handleCancel = async (id) => {
    if (
      !window.confirm(
        "¿Cancelar esta ausencia? Dejará de verse en el calendario.",
      )
    ) {
      return;
    }
    try {
      await cancelTimeOffRequest(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Error cancelando solicitud");
    }
  };

  const handleChangeType = async (id, requestType) => {
    try {
      await updateTimeOffRequest(id, { requestType });
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || "Error cambiando el tipo");
    }
  };

  const showForm = canFichaje && (!canRrhh || !selectedUserId);
  const showAllBalances = canRrhh && !selectedUserId;
  const showEmployeeCol = canRrhh && !selectedUserId;

  const employeeNameById = useMemo(
    () => buildEmployeeNameMap(balances, employees),
    [balances, employees],
  );

  const balancesWithCarry = useMemo(() => {
    const list = balances?.employees || [];
    return list.map((emp) => ({
      ...emp,
      carryInUsed: countCarryoverVacationDays(rows, year, emp.userId),
    }));
  }, [balances, rows, year]);

  const singleBalanceWithCarry =
    balancesWithCarry.length === 1 ? balancesWithCarry[0] : null;

  return (
    <TabContentShell accentColor={theme.palette.info.main}>
      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      ) : null}

      {!loading && showAllBalances ? (
        <VacationBalancesTable employees={balancesWithCarry} year={year} />
      ) : null}

      {!loading && !showAllBalances && singleBalanceWithCarry ? (
        <VacationBalanceCards balance={singleBalanceWithCarry} year={year} />
      ) : null}

      {!loading && !showAllBalances ? (
        <YearAbsenceCalendar
          year={year}
          entries={rows}
          onYearChange={setYear}
        />
      ) : null}

      {showForm ? (
        <Box sx={{ ...ttFormBlockSx, mb: 3 }}>
          <Typography variant="caption" sx={ttSectionTitleSx}>
            Nueva solicitud
          </Typography>
          <Stack spacing={1.5} alignItems="stretch">
            <TextField
              select
              size="small"
              label="Tipo"
              value={form.requestType}
              onChange={(e) =>
                setForm((f) => ({ ...f, requestType: e.target.value }))
              }
              sx={ttFilterFieldSx(theme)}
            >
              {REQUEST_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Desde"
                type="date"
                size="small"
                fullWidth
                value={form.startDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, startDate: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                sx={ttFilterFieldSx(theme)}
              />
              <TextField
                label="Hasta"
                type="date"
                size="small"
                fullWidth
                value={form.endDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, endDate: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
                sx={ttFilterFieldSx(theme)}
              />
            </Stack>
            <TextField
              label="Motivo"
              size="small"
              multiline
              minRows={2}
              value={form.reason}
              onChange={(e) =>
                setForm((f) => ({ ...f, reason: e.target.value }))
              }
              sx={ttFilterFieldSx(theme)}
            />
            <Button
              variant="contained"
              startIcon={<AddOutlinedIcon />}
              onClick={handleCreate}
              disabled={saving}
              fullWidth={isMobile}
              sx={{
                alignSelf: isMobile ? "stretch" : "center",
                fontWeight: 800,
                borderRadius: 2,
                px: 3,
                py: 1,
              }}
            >
              Solicitar
            </Button>
          </Stack>
        </Box>
      ) : null}

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <Chip
          label={`${rows.length} solicitud(es)`}
          size="small"
          sx={ttKpiChipSx}
        />
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        <Stack spacing={1.5}>
          {rows.map((r) => {
            const typeLabel =
              REQUEST_TYPES.find((t) => t.value === r.requestType)?.label ||
              r.requestType;
            const status = STATUS_COLOR[r.status] || "default";
            const actions = [];
            if (r.status === "pending" && canRrhh) {
              actions.push(
                {
                  key: "approve",
                  label: "Aprobar",
                  color: "success",
                  onClick: () => handleReview(r.id, "approved"),
                },
                {
                  key: "reject",
                  label: "Rechazar",
                  color: "error",
                  onClick: () => handleReview(r.id, "rejected"),
                },
              );
            }
            if (canCancelTimeOffRow({ status: r.status, canRrhh })) {
              actions.push({
                key: "cancel",
                label: "Cancelar",
                onClick: () => handleCancel(r.id),
              });
            }

            const employeeName = resolveEmployeeName(r, employeeNameById);

            return (
              <MobileRecordCard
                key={r.id}
                theme={theme}
                accent={statusAccentFromMuiColor(theme, status)}
                avatarName={showEmployeeCol ? employeeName : null}
                title={showEmployeeCol ? employeeName : typeLabel}
                subtitle={
                  showEmployeeCol
                    ? `${typeLabel} · ${r.startDate} → ${r.endDate}`
                    : `${r.startDate} → ${r.endDate}`
                }
                statusLabel={STATUS_LABEL[r.status] || r.status}
                statusColor={status}
                footer={
                  actions.length ? (
                    <MobileActionGrid theme={theme} actions={actions} />
                  ) : null
                }
              >
                {canChangeTimeOffTypeRow({
                  status: r.status,
                  canRrhh,
                }) ? (
                  <MobileCardMetaRow theme={theme}>
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Tipo"
                      value={r.requestType}
                      onChange={(e) => handleChangeType(r.id, e.target.value)}
                      sx={ttFilterFieldSx(theme)}
                    >
                      {REQUEST_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </MobileCardMetaRow>
                ) : null}
                {r.reason ? (
                  <MobileCardMetaRow theme={theme}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      {r.reason}
                    </Typography>
                  </MobileCardMetaRow>
                ) : null}
              </MobileRecordCard>
            );
          })}
          {!rows.length ? (
            <MobileEmptyState>Sin solicitudes</MobileEmptyState>
          ) : null}
        </Stack>
      ) : (
        <TableContainer sx={ttTableContainerSx(theme)}>
          <Table size="small" sx={ttResponsiveTableSx}>
            <TableHead>
              <TableRow>
                {showEmployeeCol ? <TableCell>Empleado</TableCell> : null}
                <TableCell>Tipo</TableCell>
                <TableCell>Desde</TableCell>
                <TableCell>Hasta</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  {showEmployeeCol ? (
                    <TableCell sx={{ fontWeight: 600 }}>
                      {resolveEmployeeName(r, employeeNameById)}
                    </TableCell>
                  ) : null}
                  <TableCell sx={{ fontWeight: 600 }}>
                    {canChangeTimeOffTypeRow({
                      status: r.status,
                      canRrhh,
                    }) ? (
                      <TextField
                        select
                        size="small"
                        value={r.requestType}
                        onChange={(e) => handleChangeType(r.id, e.target.value)}
                        sx={ttFilterFieldSx(theme)}
                      >
                        {REQUEST_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : (
                      REQUEST_TYPES.find((t) => t.value === r.requestType)
                        ?.label || r.requestType
                    )}
                  </TableCell>
                  <TableCell>{r.startDate}</TableCell>
                  <TableCell>{r.endDate}</TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_LABEL[r.status] || r.status}
                      size="small"
                      color={STATUS_COLOR[r.status] || "default"}
                      variant="outlined"
                      sx={{ height: 22, fontWeight: 700, fontSize: "0.68rem" }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Stack
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                    >
                      {r.status === "pending" && canRrhh ? (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            color="success"
                            onClick={() => handleReview(r.id, "approved")}
                            sx={{ fontWeight: 700, borderRadius: 1.25 }}
                          >
                            Aprobar
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleReview(r.id, "rejected")}
                            sx={{ fontWeight: 700, borderRadius: 1.25 }}
                          >
                            Rechazar
                          </Button>
                        </>
                      ) : null}
                      {canCancelTimeOffRow({
                        status: r.status,
                        canRrhh,
                      }) ? (
                        <Button
                          size="small"
                          onClick={() => handleCancel(r.id)}
                          sx={{ fontWeight: 700 }}
                        >
                          Cancelar
                        </Button>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length ? (
                <TableRow>
                  <TableCell
                    colSpan={(showEmployeeCol ? 1 : 0) + 5}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography color="text.secondary">
                      Sin solicitudes
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </TabContentShell>
  );
};

export default TimeOffTab;
